import React, { useState, useEffect } from "react";

import AssessmentDeterminations from "reports/AssessmentDeterminations";
import { calculateTrajectories } from "reports/TrajectoryCalculator";
import { generateAssessments, AssessmentDecision } from "reports/AssessmentGenerator";
import { useAPI } from "services/Api";
import { useNotificationManager } from "notifications/NotificationManager";

import * as MD from "reports/measures/MeasureDef";
import * as RD from "reports/ReportDef";

/**
 * ReportAssessmentValidator Component
 *
 * This component validates the accuracy of generated assessments by:
 * 1. Fetching an existing report from the backend
 * 2. Regenerating assessments using the same logic as ReportProcessor
 * 3. Comparing the newly generated assessments with the stored assessments
 * 4. Displaying a detailed comparison report
 *
 * Usage: Add to your router or navigation to access via URL like /validate-report/:reportId
 */

interface ReportSummary {
    id: number;
    patient_name: string;
    report_week: RD.ReportWeek;
    prepared_date: string;
    status: string;
}

interface ValidationResult {
    reportId: number;
    patientName: string;
    reportWeek: RD.ReportWeek;
    originalAssessments: {
        physical: string;
        behavioral: string;
        social: string;
        continuedNeeds: string;
    };
    regeneratedAssessments: {
        physical: string;
        behavioral: string;
        social: string;
        continuedNeeds: string;
    };
    matches: {
        physical: boolean;
        behavioral: boolean;
        social: boolean;
        continuedNeeds: boolean;
    };
    trajectoryCount: {
        physical: number;
        behavioral: number;
        social: number;
        continuedNeeds: number;
    };
    decisions: AssessmentDecision[];
}

const ReportAssessmentValidator: React.FC = () => {
    const API = useAPI();
    const notificationManager = useNotificationManager();

    const [reportId, setReportId] = useState<string>("");
    const [availableReports, setAvailableReports] = useState<ReportSummary[]>([]);
    const [isLoadingReports, setIsLoadingReports] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

    const fetchAvailableReports = async () => {
        setIsLoadingReports(true);

        try {
            const result = await API.api_Get(
                "[ReportAssessmentValidator]",
                `/outcome_reports/`,
                notificationManager
            );

            if (result.wasSuccessful && result.response?.data?.results) {
                // Debug: Log the first report to see the structure
                if (result.response.data.results.length > 0) {
                    console.log("Sample report structure:", result.response.data.results[0]);
                }

                const reports: ReportSummary[] = result.response.data.results.map((report: any) => {
                    // Try multiple possible locations for patient_name
                    const patientName =
                        report.sample_data?.patient_name ||
                        report.patient_name ||
                        report.patient?.name ||
                        "Unknown Patient";

                    // Try multiple possible locations for report_week
                    const reportWeek =
                        report.generated_data?.reportWeek ||
                        report.report_week ||
                        report.week ||
                        0;

                    return {
                        id: report.id,
                        patient_name: patientName,
                        report_week: reportWeek,
                        prepared_date: report.prepared_date,
                        status: report.status
                    };
                });

                // Sort by ID descending (most recent first)
                reports.sort((a, b) => b.id - a.id);

                setAvailableReports(reports);
            }
        } catch (error) {
            console.error("Failed to fetch reports:", error);
            // Don't show error notification, just fail silently
        } finally {
            setIsLoadingReports(false);
        }
    };

    useEffect(() => {
        void fetchAvailableReports();
    }, []);

    const validateReport = async () => {
        if (!reportId || isNaN(parseInt(reportId))) {
            notificationManager.addError("Please enter a valid report ID");
            return;
        }

        setIsValidating(true);
        notificationManager.clearAllNotifications();

        try {
            // Fetch the report data (read-only)
            const apiResult = await API.api_Get(
                "[ReportAssessmentValidator]",
                `/outcome_reports/${reportId}/checkout/`,
                notificationManager
            );

            if (!apiResult.wasSuccessful || !apiResult.response?.data) {
                throw new Error("Failed to fetch report data");
            }

            const reportData: RD.ReportDataApiResponse = apiResult.response.data;

            // Extract original assessments
            const generatedData = reportData.generated_data || {};
            const originalAssessments = {
                physical: generatedData.physicalAssessments || "",
                behavioral: generatedData.behavioralAssessments || "",
                social: generatedData.socialAssessments || "",
                continuedNeeds: generatedData.continuedNeedsAssessments || ""
            };

            // Calculate trajectories from raw data using shared utility
            // Trajectories are computed on-the-fly, not stored in the API response
            const reportWeek = generatedData.reportWeek || 0;
            const objectiveOutcomes = reportData.sample_data?.objective_outcomes;
            const allTrajectories = calculateTrajectories(objectiveOutcomes, reportWeek);

            const inclusions = generatedData.inclusions || {};
            const includedTrajectories = (inclusions.trajectories || {}) as Record<MD.MeasureType, boolean>;

            // Debug logging
            // console.log("Total trajectories calculated:", allTrajectories.length);
            // console.log("Inclusions object:", inclusions);
            // console.log("Included trajectories:", includedTrajectories);
            // const includedCount = Object.values(includedTrajectories).filter(v => v === true).length;
            // console.log("Number of included trajectories:", includedCount);

            // Regenerate assessments
            const regenerated = generateAssessments(allTrajectories, includedTrajectories);

            // console.log("Regenerated assessments:", regenerated);

            // Compare results
            const validationResult: ValidationResult = {
                reportId: parseInt(reportId),
                patientName: reportData.sample_data?.patient_name || "Unknown",
                reportWeek: reportData.generated_data?.reportWeek || 0,
                originalAssessments,
                regeneratedAssessments: {
                    physical: regenerated.physical,
                    behavioral: regenerated.behavioral,
                    social: originalAssessments.social + "\n\n" + regenerated.social,
                    continuedNeeds: regenerated.continuedNeeds
                },
                matches: {
                    physical: regenerated.physical === originalAssessments.physical,
                    behavioral: regenerated.behavioral === originalAssessments.behavioral,
                    social: (originalAssessments.social + "\n\n" + regenerated.social) === originalAssessments.social,
                    continuedNeeds: regenerated.continuedNeeds === originalAssessments.continuedNeeds
                },
                trajectoryCount: regenerated.counts,
                decisions: regenerated.decisions
            };

            setValidationResult(validationResult);

            const allMatch = validationResult.matches.physical && validationResult.matches.behavioral && validationResult.matches.social && validationResult.matches.continuedNeeds;
            if (allMatch) {
                notificationManager.addSuccess("✅ All assessments match! Generation logic is accurate.");
            } else {
                notificationManager.addError("⚠️ Some assessments don't match. Review differences below.");
            }

        } catch (error) {
            console.error("Validation error:", error);
            notificationManager.addError(`Validation failed: ${error.message}`);
        } finally {
            setIsValidating(false);
        }
    };

    const renderComparisonSection = (title: string, original: string, regenerated: string, matches: boolean, count: number) => {
        return (
            <div className="mb-6 p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Trajectories: {count}</span>
                        {matches ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">✓ Match</span>
                        ) : (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">⚠ Differs</span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Original (Stored)</h4>
                        <div className="p-3 bg-gray-50 border border-gray-300 rounded text-sm whitespace-pre-wrap font-mono">
                            {original || "(None)"}
                        </div>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Regenerated</h4>
                        <div className={`p-3 border rounded text-sm whitespace-pre-wrap font-mono ${matches ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
                            {regenerated || "(None)"}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDecisionAccounting = (decisions: AssessmentDecision[]) => {
        return (
            <div className="mb-6 p-4 border rounded-lg bg-white">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Assessment Determinations</h3>
                <AssessmentDeterminations decisions={decisions} />
            </div>
        );
    };

    return (
        <div className="w-full h-full p-6 overflow-y-auto bg-gray-50">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Report Assessment Validator</h1>
                    <p className="text-gray-600 mb-6">
                        This tool validates the accuracy of generated assessments by comparing stored assessments
                        with freshly regenerated ones using the same logic from ReportProcessor.
                    </p>

                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <label htmlFor="reportSelect" className="text-sm font-medium text-gray-700 w-28">
                                Select Report:
                            </label>
                            <select
                                id="reportSelect"
                                value={reportId}
                                onChange={(e) => setReportId(e.target.value)}
                                disabled={isValidating || isLoadingReports}
                                className="w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100"
                            >
                                <option value="">
                                    {isLoadingReports ? "Loading reports..." : "-- Select a report --"}
                                </option>
                                {availableReports.map((report) => (
                                    <option key={report.id} value={report.id}>
                                        ID: {report.id} | {report.patient_name} | Week {report.report_week}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-3">
                            <label htmlFor="reportId" className="text-sm font-medium text-gray-700 w-28">
                                Or Enter ID:
                            </label>
                            <input
                                id="reportId"
                                type="text"
                                value={reportId}
                                onChange={(e) => setReportId(e.target.value)}
                                placeholder="e.g. 843"
                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                                disabled={isValidating}
                            />
                            <button
                                onClick={validateReport}
                                disabled={isValidating || !reportId}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                {isValidating ? "Validating..." : "Validate Report"}
                            </button>
                        </div>
                    </div>
                </div>

                {validationResult && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="mb-6 pb-6 border-b">
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Validation Results</h2>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-600">Report ID:</span>
                                    <span className="ml-2 font-medium">{validationResult.reportId}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Patient:</span>
                                    <span className="ml-2 font-medium">{validationResult.patientName}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">Week:</span>
                                    <span className="ml-2 font-medium">{validationResult.reportWeek}</span>
                                </div>
                            </div>
                        </div>

                        {renderDecisionAccounting(validationResult.decisions)}

                        {renderComparisonSection(
                            "Physical Assessments",
                            validationResult.originalAssessments.physical,
                            validationResult.regeneratedAssessments.physical,
                            validationResult.matches.physical,
                            validationResult.trajectoryCount.physical
                        )}

                        {renderComparisonSection(
                            "Behavioral Assessments",
                            validationResult.originalAssessments.behavioral,
                            validationResult.regeneratedAssessments.behavioral,
                            validationResult.matches.behavioral,
                            validationResult.trajectoryCount.behavioral
                        )}

                        {renderComparisonSection(
                            "Social Assessments",
                            validationResult.originalAssessments.social,
                            validationResult.regeneratedAssessments.social,
                            validationResult.matches.social,
                            validationResult.trajectoryCount.social
                        )}

                        {renderComparisonSection(
                            "Continued Needs Assessments",
                            validationResult.originalAssessments.continuedNeeds,
                            validationResult.regeneratedAssessments.continuedNeeds,
                            validationResult.matches.continuedNeeds,
                            validationResult.trajectoryCount.continuedNeeds
                        )}

                        <div className="mt-6 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                            <h4 className="text-sm font-medium text-blue-900 mb-3">📊 How to Interpret Results</h4>
                            <div className="space-y-2 text-sm text-blue-800">
                                <p><strong>✓ Match (Green):</strong> The regenerated assessment exactly matches the stored assessment. The logic is working correctly.</p>
                                <p><strong>⚠ Differs (Yellow):</strong> The assessments don't match. This could mean:</p>
                                <ul className="ml-6 list-disc space-y-1">
                                    <li><strong>(None) in Regenerated:</strong> No trajectories met the criteria for this category. Check browser console for debugging info.</li>
                                    <li><strong>Text differences:</strong> The generation logic may have changed since this report was created, or there may be a bug.</li>
                                    <li><strong>Different trajectories included:</strong> The inclusion settings may have changed.</li>
                                </ul>
                                <p className="mt-3"><strong>Trajectory Count:</strong> Shows how many trajectories were categorized for each assessment type during regeneration.</p>
                            </div>
                        </div>

                        <div className="mt-4 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Note about Social Assessments</h4>
                            <p className="text-sm text-gray-600">
                                Social assessments are manually entered by the preparer and are not auto-generated,
                                so they are not included in this validation comparison.
                            </p>
                        </div>

                        <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                            <h4 className="text-sm font-medium text-amber-900 mb-2">🔍 Debugging</h4>
                            <p className="text-sm text-amber-800">
                                If you see "(None)" in the regenerated assessments, open the browser console (F12) to see detailed debugging information about trajectories and inclusions.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportAssessmentValidator;
